import React from 'react'
import PropTypes from 'prop-types'
import { Formik, Form as FormikForm } from 'formik'
import mapValues from 'lodash/mapValues'
import { retirementBudgetTotal, currentBudgetTotal } from '@components/DetailedBudget/totals'

import { Card, CardContent } from '@components/layout/Card'
import { TabHeader } from '@components/TabHeader'
import { percentage, R } from '@utils/currency'

import { JointBudgetFields } from './JointBudgetFields'
import { CurrentIncomeBreakdown } from './CurrentIncomeBreakdown'
import { CurrentExpenseBreakdown } from './CurrentExpenseBreakdown'
import { Footer } from './Footer'


export const JointBudget = ({ jointBudget, participantsQueries, config, crudActions, jointForecast }) => {

  return (
    <Card className='budget'>
      <TabHeader title={`Let's plan your budget`} />
      <Formik
        enableReinitialize
        onSubmit={async (values) => {
          await submitForm(values, crudActions)
        }}
        initialValues={initialValues(jointBudget)}>
        <FormikForm className='app-form'>
          <CardContent>
            <fieldset>
              <legend>Joint budget</legend>
              <JointBudgetFields
                participantsQueries={participantsQueries}
                jointBudget={jointBudget}
                config={config}
              />
            </fieldset>
            <fieldset>
              <legend>Current income breakdown</legend>
              <CurrentIncomeBreakdown
                config={config}
                participantsQueries={participantsQueries}
                jointForecast={jointForecast}
              />
            </fieldset>
            <fieldset>
              <legend>Current expense breakdown</legend>
              <CurrentExpenseBreakdown
                config={config}
                participantsQueries={participantsQueries}
                jointForecast={jointForecast}
              />
            </fieldset>
          </CardContent>
          <Footer />
        </FormikForm>
      </Formik>
    </Card>
  )
}

// NOTE: the decimal values in jointBudget.joint_budget_distributions need to be converted to percentages
const initialValues = (jointBudget) => {
  return {
    ...jointBudget,
    joint_budget_distributions_attributes: jointBudget.joint_budget_distributions_attributes.map((distribution) => ({
      ...distribution,
      value: percentage(distribution?.value).value,
    }))
  }
}

const submitForm = async (values, crudActions) => {
  const { detailed_budget } = values
  const isDetailedBudget = values.selected_budget_type === 'detailed'


  const detailedBudget = mapValues(detailed_budget, (budgetItem) => budgetItem.map((value) => R(value).value))

  const netMonthlyCurrentBudgetReal = isDetailedBudget
    ? R(currentBudgetTotal(detailedBudget)).value
    : R(values.net_monthly_current_budget_real).value

  const netMonthlyRetirementBudgetReal = values.isDetailedBudget
    ? R(retirementBudgetTotal(detailedBudget)).value
    : R(values.net_monthly_retirement_budget_real).value

  const jointBudget = {
    ...values,
    detailed_budget: detailedBudget,
    net_monthly_current_budget_real: netMonthlyCurrentBudgetReal,
    net_monthly_retirement_budget_real: values.use_current_expense_for_retirement
      ? netMonthlyCurrentBudgetReal
      : netMonthlyRetirementBudgetReal,

    budgets_attributes: values.budgets_attributes.map((budget) => ({
      ...budget,
      gross_monthly_current_income: R(budget?.gross_monthly_current_income).value,
    })),

    //NOTE: percentage values need to be converted back to decimals before submission
    joint_budget_distributions_attributes: values.joint_budget_distributions_attributes.map((distribution) => ({
      ...distribution,
      value: parseFloat(distribution?.value / 100),
    }))
  }
  await crudActions.update(jointBudget)
}

JointBudget.propTypes = {
  jointBudget: PropTypes.object.isRequired,
  participantsQueries: PropTypes.object.isRequired,
  config: PropTypes.object.isRequired,
  crudActions: PropTypes.object.isRequired,
  jointForecast: PropTypes.object.isRequired,
}
